import { ReactElement, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useProjectStore } from "../../stores/project.store";
import { getStepRows } from "../../utils/getStepRows";

export interface TrajectoryAnalysisData {
  cluster_column: string;
  root_column: string;
  root_value: string;
  use_rep: string;
}

interface Props {
  onSubmit?: (data: TrajectoryAnalysisData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

const TrajectoryAnalysisForm = ({ onSubmit, onBack, className = "" }: Props): ReactElement => {
  const [formError, setFormError] = useState("");
  const stepResults = useProjectStore((state) => state.stepResults);
  const backendResult = useProjectStore((state) => state.backendResult);
  const sourceRows = useMemo(() => {
    const preferred = getStepRows(stepResults?.["cell-type-annotation"]);
    if (preferred.length) return preferred;
    for (const result of Object.values(stepResults ?? {})) {
      const rows = getStepRows(result);
      if (rows.length && rows.some((row) => row?.annotation != null || row?.leiden != null)) return rows;
    }
    return getStepRows(backendResult);
  }, [stepResults, backendResult]);

  const columnOptions = useMemo(() => {
    const keys = new Set<string>();
    sourceRows.slice(0, 500).forEach((row) => Object.keys(row ?? {}).forEach((key) => keys.add(key)));
    return Array.from(keys).filter((key) => !["cell_id", "UMAP1", "UMAP2", "umap1", "umap2"].includes(key));
  }, [sourceRows]);

  const [formData, setFormData] = useState<TrajectoryAnalysisData>({
    cluster_column: "leiden",
    root_column: "annotation",
    root_value: "",
    use_rep: "X_pca",
  });
  const rootValues = useMemo(
    () => Array.from(new Set(sourceRows.map((row) => String(row?.[formData.root_column] ?? "").trim()).filter(Boolean))).sort(),
    [sourceRows, formData.root_column],
  );

  const update = (field: keyof TrajectoryAnalysisData, value: string) => {
    setFormError("");
    setFormData((current) => ({
      ...current,
      [field]: value,
      ...(field === "root_column" ? { root_value: "" } : {}),
    }));
  };

  const submit = () => {
    if (!formData.root_value.trim()) {
      setFormError("Select or enter a biological root population before running trajectory analysis.");
      return;
    }
    onSubmit?.(formData);
  };

  return (
    <div className={`p-6 h-full flex flex-col justify-between ${className}`} style={{ backgroundColor: "#F5F5F5" }}>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Run PAGA and DPT on the existing clustered and annotated result. This step does not rerun preprocessing, neighbors, UMAP, clustering, or annotation.
        </Typography>
        {!sourceRows.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Saved annotation values could not be previewed. You can still enter the exact root population below.
          </Alert>
        )}
      </Box>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
        {formError && <Alert severity="warning">{formError}</Alert>}
        <TextField
          fullWidth label="Cluster column" value={formData.cluster_column}
          onChange={(event) => update("cluster_column", event.target.value)}
          helperText="Existing cluster labels in the processed file (default: leiden)"
          sx={{ backgroundColor: "white" }}
        />
        <FormControl fullWidth sx={{ backgroundColor: "white" }}>
          <InputLabel>Root annotation column</InputLabel>
          <Select
            value={formData.root_column}
            label="Root annotation column"
            onChange={(event) => update("root_column", event.target.value)}
          >
            {(columnOptions.includes("annotation") ? columnOptions : ["annotation", ...columnOptions]).map((column) => (
              <MenuItem key={column} value={column}>{column}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {rootValues.length ? (
          <FormControl fullWidth required sx={{ backgroundColor: "white" }}>
            <InputLabel>Biological root population</InputLabel>
            <Select
              value={formData.root_value}
              label="Biological root population"
              onChange={(event) => update("root_value", event.target.value)}
            >
              {rootValues.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </Select>
          </FormControl>
        ) : (
          <TextField
            fullWidth required label="Biological root population" value={formData.root_value}
            onChange={(event) => update("root_value", event.target.value)}
            helperText={`Exact value from ${formData.root_column}`}
            sx={{ backgroundColor: "white" }}
          />
        )}
        <FormControl fullWidth sx={{ backgroundColor: "white" }}>
          <InputLabel>Representation</InputLabel>
          <Select value={formData.use_rep} label="Representation" onChange={(event) => update("use_rep", event.target.value)}>
            <MenuItem value="X_pca">X_pca</MenuItem>
            <MenuItem value="X_umap">X_umap</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>Back</Button>
        <Button variant="contained" onClick={submit}>
          Run trajectory
        </Button>
      </Box>
    </div>
  );
};

export default TrajectoryAnalysisForm;
